import { useCallback, useEffect, useRef, useState } from "react";

const useCountdownTimer = ({
  timer,
  interval = 1000,
  autostart = false,
  expireImmediate = false,
  resetOnExpire = true,
  onExpire,
  onReset,
}) => {
  const [countdown, setCountdown] = useState(timer);
  const [canStart, setCanStart] = useState(autostart);
  const [isRunning, setIsRunning] = useState(false);

  const start = useCallback(() => {
    // must explicitly call reset() before starting an expired timer
    if (countdown !== 0) {
      setCanStart(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function pause() {
    setCanStart(false);
    setIsRunning(false);
  }

  function initStopped(time) {
    setCanStart(false);
    setIsRunning(false);
    setCountdown(time);
  }

  const reset = useCallback(() => {
    initStopped(timer);
    if (onReset && typeof onReset === "function") {
      onReset();
    }
  }, [timer, onReset]);

  const expire = useCallback(() => {
    initStopped(resetOnExpire ? timer : 0);
    if (onExpire && typeof onExpire === "function") {
      onExpire();
    }
  }, [timer, onExpire, resetOnExpire]);

  const countdownRef = useRef(timer);
  useEffect(() => {
    countdownRef.current = countdown;
  }, [countdown]);

  useEffect(() => {
    function tick() {
      if (
        countdownRef.current / 1000 <= 0 ||
        (expireImmediate && (countdownRef.current - interval) / 1000 <= 0)
      ) {
        expire();
      } else {
        setCountdown((prev) => prev - interval);
      }
    }

    let id;
    if (canStart) {
      id = setInterval(tick, interval);
      if (!isRunning) {
        setIsRunning(true);
      }
    }
    return () => clearInterval(id);
  }, [expire, canStart, interval, expireImmediate, isRunning]);

  return {
    countdown,
    startCountdown: start,
    resetCountdown: reset,
    pauseCountdown: pause,
    isCountdownRunning: isRunning,
  };
};
export default useCountdownTimer;
